import React from 'react';
import {usePgMutationForm} from '@practicegenius/domain/src/hooks/usePgForm';
import * as Yup from 'yup';
import {assertNever} from '@practicegenius/pg-util/src/TypescriptUtils';
import {addQueryParams} from '@practicegenius/pg-util/src/UrlUtils';
import {
  Button,
  ButtonVariant,
} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {Input} from '@practicegenius/pg-ui/src/base/Material/components/Input';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {SxImg} from '@practicegenius/pg-ui/src/base/Material/components/StyleableBaseComponents';
import * as R from 'ramda';
import {TemplateLottie} from '@practicegenius/pg-animation/src/Template';
import confetti from '@practicegenius/pg-animation/src/lotties/14162-taras-chenenko-success-confetti.json';
import {useSnackbar} from 'notistack';
import {ValidationException} from '@practicegenius/http-clients-js/src/exceptions';
import {useMutation} from '@tanstack/react-query';
import {
  FormikMemorableDateInput,
  MemorableDate$Form,
  makeMemorableDateValidationSchema,
} from '@practicegenius/pg-ui/src/base/Material/components/MemorableDateInput';
import {FormikErrors} from 'formik';
import {format, subYears} from 'date-fns';
import {CommonDateFormats} from '@practicegenius/pg-util/src/DateUtils';
import {Link, useHistory} from 'react-router-dom';
import {inMobileApp} from '@practicegenius/pg-ui/src/utils/MobileApp';
import {reloadPageTo} from '@practicegenius/pg-ui/src/utils/navigation';
import {usePhpJs} from '../../../utils/php-js';
import success from '../../../components/SvgImage/svgs-raw/registrationSuccess.svg';
import {TextHr} from '../../../components/TextHr';
import {PracticeLogo} from '../../../components/PracticeLogo';
import {globalRoutes} from '../../../shared/globalRoutes';
import logger from '../../../utils/logger';
import {coreApiClientNoAuth} from '../../../http/httpClientsJs';
import {parseCheckInDataFromQueryString} from '../../earn-points/data/checkIn';
import {useClearReferrerInSession} from '../../../shared/queries/global';

// TODO this should be considered temporary as an overhaul of all the buttons in sites-dot (at least on the React side) will soon be on the table
// see https://practicegenius.atlassian.net/browse/PAT-15063
const StyledButton = styled(Button)(({theme}) => ({
  height: '56px',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const NarrowContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '320px',
});

const FormInputWrapper = styled(NarrowContent)(({theme}) => ({
  gap: theme.spacing(),
}));

const EndButtonsWrapper = styled(NarrowContent)(({theme}) => ({
  gap: theme.spacing(3),
}));

type CheckInUserData$Form = {
  firstname: string;
  lastname: string;
  birthdate: MemorableDate$Form;
};

const serializeCheckInUserData = (userData: CheckInUserData$Form) => ({
  ...userData,
  birthdate: userData.birthdate.composedDate
    ? format(userData.birthdate.composedDate, CommonDateFormats['2020-01-20'])
    : null,
});

/**
 * This code is intentionally not human readable to limit the ability of a malicious user to parse a user's existence from the response
 * This comment is stripped out of the bundle for production, so it's safe to decode them here:
 * 0: No User Match
 * 1: Single Unregistered User Match
 * 2: Single Registered User Match
 * 3: Multiple User Matches
 *
 * This corresponds with api-core's UnregisteredUserCheckInCode enum, but it is intentionally not represented as a TS enum
 *   to avoid leaking the enum values in the bundle
 */
type UnregisteredUserCheckInCode = 0 | 1 | 2 | 3;

type UnregisteredUserCheckInResponse = {
  code: UnregisteredUserCheckInCode;
  registrationToken?: string;
  /** The amount of points potentially awarded by the scan */
  pointsAward: number;
};

const parseUnregisteredUserCheckInResponse = (data: UnknownServerData) =>
  objectKeysToCamelCaseDeep(data) as UnregisteredUserCheckInResponse;

type ActiveScreen =
  | {
      type: 'entry' | 'form';
    }
  | ({
      type: 'result';
      userFirstname: string;
    } & UnregisteredUserCheckInResponse);

const ScreenHeading: React.FCC = ({children}) => (
  <Typography variant="h5" textAlign="center">
    {children}
  </Typography>
);

const EntryScreen: React.FC<{
  onConfirmCheckIn: () => void;
}> = ({onConfirmCheckIn}) => {
  return (
    <>
      <ScreenHeading>
        How would you like to claim your points today?
      </ScreenHeading>
      <EndButtonsWrapper
        sx={{
          mt: 6,
        }}
      >
        <StyledButton
          color="primary"
          variant={ButtonVariant.contained}
          onClick={onConfirmCheckIn}
        >
          Name and Birthdate
        </StyledButton>
        <TextHr>or</TextHr>
        <StyledButton
          color="primary"
          variant={ButtonVariant.outlined}
          data-external
          component={Link}
          // @ts-expect-error
          to={{
            pathname: globalRoutes.login.index,
            state: {showBackButton: true},
          }}
        >
          Log In
        </StyledButton>
      </EndButtonsWrapper>
    </>
  );
};

const checkInFormValidationSchema = Yup.object().shape({
  firstname: Yup.string().required('First Name is required'),
  lastname: Yup.string().required('Last Name is required'),
  birthdate: makeMemorableDateValidationSchema({
    enhanceDateSchema: schema =>
      schema
        .min(
          // Disallow dates more than 130 years in the
          subYears(new Date(), 130),
          'Birthdate is too far in the past',
        )
        .max(
          // disallow dates in the future
          new Date(),
          'Birthdate cannot be in the future',
        ),
  }),
});

const FormScreen: React.FC<{
  onGoBack: () => void;
  onSubmitSuccess: (result: {
    formData: CheckInUserData$Form;
    response: UnregisteredUserCheckInResponse;
  }) => void;
  checkInType: keyof typeof globalRoutes.points.checkin;
  recipeId: number;
  practiceId: number;
}> = ({onGoBack, onSubmitSuccess, checkInType, recipeId, practiceId}) => {
  const router = useHistory();
  const {enqueueSnackbar} = useSnackbar();
  const checkInMutation = useMutation({
    mutationFn: (userInfo: CheckInUserData$Form) =>
      coreApiClientNoAuth._unsafe
        .post(`/user/check-in/${checkInType}`, {
          practice_id: practiceId,
          recipe_id: recipeId,
          user_data: serializeCheckInUserData(userInfo),
        })
        .then(response => ({
          formData: userInfo,
          response: parseUnregisteredUserCheckInResponse(response.data),
        })),
  });

  const formik = usePgMutationForm({
    formikConfig: {
      initialValues: {
        firstname: '',
        lastname: '',
        birthdate: {
          day: '',
          month: '',
          year: '',
        },
      } as CheckInUserData$Form,
      validationSchema: checkInFormValidationSchema,
      validateOnChange: false,
      onSubmit: form => {
        checkInMutation.mutate(form);
      },
    },
    mutationResult: checkInMutation,
    onSubmitSuccess,
    transformServerErrors: err => {
      return R.applySpec({
        firstname: R.path(['user_data.firstname', 0]),
        lastname: R.path(['user_data.lastname', 0]),
        birthdate: {
          composedDate: R.path(['user_data.birthdate', 0]),
        },
      })(err) as FormikErrors<CheckInUserData$Form>;
    },
    onSubmitError: err => {
      if (err instanceof ValidationException) {
        // If recipe_id is in validation errors, then we must be on an invalid recipe
        if ('recipe_id' in err.errors) router.replace(globalRoutes.login.index);
      } else {
        logger.error(
          'Got an unexpected error when attempting unauthenticated user check-in',
          err,
        );
        const message =
          err instanceof Error ? err.message : 'An error occurred';
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    },
  });

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 4,
      }}
    >
      <ScreenHeading>Claim with your Name and Birthday</ScreenHeading>
      <FormInputWrapper>
        <Input
          label="First Name"
          {...formik.getFieldProps('firstname')}
          autoFocus
        />

        <Input label="Last Name" {...formik.getFieldProps('lastname')} />
        <FormikMemorableDateInput
          form={formik}
          dateFieldName="birthdate"
          monthFieldPrefix="Birth"
        />
      </FormInputWrapper>
      <EndButtonsWrapper
        sx={{
          gap: 2,
        }}
      >
        <StyledButton
          variant={ButtonVariant.contained}
          color="primary"
          type="submit"
          loading={checkInMutation.isPending}
        >
          Submit
        </StyledButton>
        <StyledButton
          variant={ButtonVariant.outlined}
          color="primary"
          onClick={onGoBack}
          loading={checkInMutation.isPending}
        >
          Back
        </StyledButton>
      </EndButtonsWrapper>
    </Box>
  );
};

const makeRegistrationLinkWithToken = (token: string) =>
  addQueryParams(globalRoutes.account.register.RegistrationMethods, {
    token,
  });

const SuccessGraphic = () => {
  const [showAnimation, setShowAnimation] = React.useState(false);

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {showAnimation && (
        <Box
          sx={{
            position: 'absolute',
            top: -216,
            left: -137,
            transform: 'rotate(45deg)',
          }}
        >
          <TemplateLottie animationData={confetti} width={480} />
        </Box>
      )}
      <SxImg
        sx={{
          width: 136,
        }}
        src={success}
        alt="Success"
        onLoad={() => setShowAnimation(true)}
      />
    </Box>
  );
};

const CheckInResultScreen: React.FC<
  Extract<ActiveScreen, {type: 'result'}>
> = ({userFirstname, pointsAward, registrationToken, code}) => {
  const verified = code !== 3;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        gap: 6,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <>
          <ScreenHeading>Hi {userFirstname} 👋</ScreenHeading>
          <ScreenHeading>
            {verified
              ? `You scanned the QR code for ${pointsAward} points!`
              : "We couldn't verify your account"}
          </ScreenHeading>
          <Typography color="textSecondary" textAlign="center">
            {verified
              ? "If eligible, they'll appear in your account soon. Log in or sign up to see your points."
              : `Please contact ${window.phpJs.practice.practice_name} for help.`}
          </Typography>
          {verified && (
            <Box sx={{mt: 2}}>
              <SuccessGraphic />
            </Box>
          )}
        </>
      </Box>

      <EndButtonsWrapper
        sx={{
          justifySelf: 'flex-end',
        }}
      >
        {(() => {
          const makeButtonText = (text: string) =>
            `${text}${verified ? ' To View Points' : ''}`;

          const {primaryButtonProps, secondaryButtonProps} = registrationToken
            ? ({
                primaryButtonProps: {
                  href: makeRegistrationLinkWithToken(registrationToken),
                  children: makeButtonText('Sign Up'),
                },
                secondaryButtonProps: {
                  variant: ButtonVariant.text,
                  children: 'Exit and Sign Up Later',
                },
              } as const)
            : ({
                primaryButtonProps: {
                  component: Link,
                  children: makeButtonText('Log In'),
                  to: {
                    pathname: globalRoutes.login.index,
                    state: {
                      showBackButton: true,
                    },
                  },
                },
                secondaryButtonProps: {
                  variant: ButtonVariant.outlined,
                  children: 'Done',
                },
              } as const);

          return (
            <>
              <StyledButton
                variant={ButtonVariant.contained}
                color="primary"
                data-external
                {...primaryButtonProps}
              />
              <StyledButton
                href={globalRoutes.index}
                data-external
                {...secondaryButtonProps}
              />
            </>
          );
        })()}
      </EndButtonsWrapper>
    </Box>
  );
};

export const UnauthenticatedCheckIn = () => {
  const {practice_id: practiceId} = usePhpJs(x => x.practice) as {
    practice_id: number;
    practice_name: string;
  };

  const checkIn = React.useMemo(
    () => parseCheckInDataFromQueryString(window.location.search),
    [],
  );

  // Setup initial state
  const [activeScreen, setActiveScreen] = React.useState<ActiveScreen>({
    type: 'entry',
  });
  const clearReferrerInSession = useClearReferrerInSession();

  // If checkIn data did not parse or practice id does not correspond to a real practice then redirect to the login form
  if (!checkIn || practiceId <= 0) {
    reloadPageTo('/login');
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        height: '100%',
        pb: inMobileApp ? 8 : 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {
          // While not in the mobile app this logo is rendered by the Kohana layout
          inMobileApp && (
            <PracticeLogo
              sx={{
                width: {
                  xs: '100%',
                  sm: '335px',
                },
                my: 2,
              }}
            />
          )
        }
        {(() => {
          switch (activeScreen.type) {
            case 'entry':
              return (
                <EntryScreen
                  onConfirmCheckIn={() => setActiveScreen({type: 'form'})}
                />
              );
            case 'form':
              return (
                <FormScreen
                  checkInType={checkIn.type}
                  recipeId={checkIn.id}
                  practiceId={practiceId}
                  onGoBack={() => setActiveScreen({type: 'entry'})}
                  onSubmitSuccess={({formData, response}) => {
                    // Usually when a scan lands on this page they will have been redirected to this landing page
                    //  and the actual check-in page will be there referrer in the session.

                    // We want to leave this referrer in place if a user is unregistered because this scan
                    //   does not award them any points.

                    // However, a single matched user will be awarded points by entering their information.
                    // If the entered info matches a user, we want to clear this referrer so that when the user logs in
                    //   or registers  they won't see the points screen again (which would trigger another check-in attempt)
                    if (response.code !== 0) {
                      clearReferrerInSession.mutate();
                    }

                    return setActiveScreen({
                      type: 'result',
                      userFirstname: formData.firstname,
                      ...response,
                    });
                  }}
                />
              );
            case 'result':
              return <CheckInResultScreen {...activeScreen} />;

            default:
              assertNever(activeScreen);
          }
        })()}
      </Box>
    </Box>
  );
};
